import {
	EditThesisContentInput,
	RejectThesisInput,
	ReorderThesesInput,
	ReviewDocumentFormat,
} from 'modules/contestacao/pages/review/contestacao-review.model';
import { DOCUMENT_FONT_CATALOG } from 'components/document-editor/document-typography.const';
import {
	ContestacaoTemplatePreviewDTO,
	CreateContestacaoTemplateDTO,
	UpdateContestacaoTemplateDTO,
} from 'services/contestacao-template/contestacao-template.model';
import { UpdateProductCustomContestacaoDTO } from 'services/product-custom-contestacao/product-custom-contestacao.model';
import { MockRoute, mockRoute, paginate } from '../mock-api';
import { findJobEntry } from '../shared/mock-job-state';
import { buildPreviewDocument, buildReviewDocument } from './contestacao-document';
import { DEFAULT_PROCESS_NUMBER } from './contestacao.const';
import {
	approveThesis,
	bulkApproveTopic,
	completeReview,
	editThesisContent,
	getReview,
	openReview,
	rejectThesis,
	removeThesis,
	reorderTheses,
} from './contestacao-review';
import {
	activeTemplateFor,
	cloneTemplate,
	createTemplate,
	deleteTemplate,
	demoteTemplate,
	findTemplate,
	listCustomTemplates,
	listTemplates,
	promoteTemplate,
	updateTemplate,
} from './contestacao-templates';

const REVIEW = '/jobs/:id/review';

/** Revisão da Contestação (`ContestacaoReviewService`) */
const REVIEW_ROUTES: MockRoute[] = [
	mockRoute('GET', `${REVIEW}/document`, ({ params, query }) => {
		const review = getReview(params['id']);
		const productSetupId = findJobEntry(params['id'])?.productSetupId ?? '';
		const format = (query.get('format') ?? 'pdf') as ReviewDocumentFormat;
		return buildReviewDocument(review, activeTemplateFor(productSetupId).config, format);
	}),
	mockRoute('GET', REVIEW, ({ params }) => getReview(params['id'])),
	mockRoute('POST', REVIEW, ({ params }) => openReview(params['id'])),
	mockRoute('POST', `${REVIEW}/complete`, ({ params }) => completeReview(params['id'])),
	mockRoute('POST', `${REVIEW}/theses/:thesisId/approve`, ({ params }) =>
		approveThesis(params['id'], params['thesisId']),
	),
	mockRoute<RejectThesisInput>('POST', `${REVIEW}/theses/:thesisId/reject`, ({ params, body }) =>
		rejectThesis(params['id'], params['thesisId'], body),
	),
	mockRoute<RejectThesisInput>('POST', `${REVIEW}/theses/:thesisId/remove`, ({ params, body }) =>
		removeThesis(params['id'], params['thesisId'], body),
	),
	mockRoute<EditThesisContentInput>(
		'PATCH',
		`${REVIEW}/theses/:thesisId/content`,
		({ params, body }) => editThesisContent(params['id'], params['thesisId'], body.content),
	),
	mockRoute('POST', `${REVIEW}/topics/:topicId/bulk-approve`, ({ params }) =>
		bulkApproveTopic(params['id'], params['topicId']),
	),
	mockRoute<ReorderThesesInput>('PATCH', `${REVIEW}/topics/:topicId/order`, ({ params, body }) =>
		reorderTheses(params['id'], params['topicId'], body.orderedThesisIds),
	),
];

/** Templates versionados — visão do admin (`ContestacaoTemplateService`) */
const TEMPLATE_ROUTES: MockRoute[] = [
	mockRoute('GET', '/product-contestacao-templates', ({ query }) =>
		paginate(listTemplates(query), query),
	),
	mockRoute<CreateContestacaoTemplateDTO>('POST', '/product-contestacao-templates', ({ body }) =>
		createTemplate(body),
	),
	mockRoute('GET', '/product-contestacao-templates/:id', ({ params }) =>
		findTemplate(params['id']),
	),
	mockRoute<UpdateContestacaoTemplateDTO>(
		'PATCH',
		'/product-contestacao-templates/:id',
		({ params, body }) => updateTemplate(params['id'], body),
	),
	mockRoute('DELETE', '/product-contestacao-templates/:id', ({ params }) =>
		deleteTemplate(params['id']),
	),
	mockRoute('POST', '/product-contestacao-templates/:id/promote', ({ params }) =>
		promoteTemplate(params['id']),
	),
	mockRoute('POST', '/product-contestacao-templates/:id/demote', ({ params }) =>
		demoteTemplate(params['id']),
	),
	mockRoute('POST', '/product-contestacao-templates/:id/clone', ({ params }) =>
		cloneTemplate(params['id']),
	),
	mockRoute<ContestacaoTemplatePreviewDTO>(
		'POST',
		'/product-contestacao-templates/:id/preview',
		({ params, body }) => buildPreviewDocument(findTemplate(params['id']), body),
	),
];

/** Templates do product setup — visão do gestor (`ProductCustomContestacaoService`) */
const CUSTOM_TEMPLATE_ROUTES: MockRoute[] = [
	mockRoute('GET', '/product-custom-contestacao/document-typesetting/default', ({ query }) => ({
		typesetting: activeTemplateFor(query.get('productSetupId') ?? '').config?.typesetting ?? null,
		// O Hub semeia o `processNumber` de templates novos com este default do backend
		processNumber: DEFAULT_PROCESS_NUMBER,
	})),
	// No backend, as fontes instaladas no servidor; aqui, as que o editor carrega da CDN.
	mockRoute('GET', '/product-custom-contestacao/document-typesetting/fonts', () => ({
		families: DOCUMENT_FONT_CATALOG.map(({ family }) => family),
	})),
	mockRoute('GET', '/product-custom-contestacao', ({ query }) =>
		paginate(listCustomTemplates(query), query),
	),
	mockRoute('POST', '/product-custom-contestacao/:id/clone', ({ params }) =>
		cloneTemplate(params['id']),
	),
	mockRoute<UpdateProductCustomContestacaoDTO>(
		'PATCH',
		'/product-custom-contestacao/:id',
		({ params, body }) => updateTemplate(params['id'], body),
	),
	mockRoute('DELETE', '/product-custom-contestacao/:id', ({ params }) =>
		deleteTemplate(params['id']),
	),
	mockRoute('POST', '/product-custom-contestacao/:id/promote', ({ params }) =>
		promoteTemplate(params['id']),
	),
	mockRoute<ContestacaoTemplatePreviewDTO>(
		'POST',
		'/product-custom-contestacao/:id/preview',
		({ params, body }) => buildPreviewDocument(findTemplate(params['id']), body),
	),
];

export const CONTESTACAO_MOCK_ROUTES: MockRoute[] = [
	...REVIEW_ROUTES,
	...TEMPLATE_ROUTES,
	...CUSTOM_TEMPLATE_ROUTES,
];
